import { faroCoreApiClient } from "@api/faro-core-api-client";
import { CompanyProjectCreation } from "@pages/create-project/project-creation-context-types";
import { useState } from "react";
import { AdmProject, ErrorHandler, ProjectId } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";

interface RequestCreateProjectParams {
  /** Metadata to create the project with */
  createProjectPayload: FaroCoreApiTypes.CreateProjectPayload;

  /** The company in which the project is going to be created for */
  subject: CompanyProjectCreation;
}

interface ReturnProps {
  /** Indicator whether there is currently a project creation request in process */
  isRequestingProjectCreation: boolean;

  /** Request the project creation on the backend with the provided parameters */
  requestCreateProject: (params: RequestCreateProjectParams) => Promise<void>;

  /** ID of the newly created project */
  createdProjectId?: ProjectId;
}

/** Hook that should be used to create a project for companies. */
export function useCreateProject({ errorHandler }: ErrorHandler): ReturnProps {
  const { trackEvent } = useTrackEvent();

  const [isRequestingProjectCreation, setIsRequestingProjectCreation] =
    useState(false);

  const [createdProjectId, setCreatedProjectId] = useState<ProjectId>();

  async function requestCreateProject({
    createProjectPayload,
    subject,
  }: RequestCreateProjectParams): Promise<void> {
    trackEvent({
      name: WorkspaceEvents.createProject,
      props: {
        nameLength: createProjectPayload.name.length,
        addressLength: createProjectPayload.address.length,
        projectArea: createProjectPayload.area.amount,
        projectAreaUnit: createProjectPayload.area.unit,
        isStandalone: createProjectPayload.standalone,
        projectManagerId: createProjectPayload.projectManager,
      },
    });

    setIsRequestingProjectCreation(true);

    try {
      const createdProject: AdmProject =
        await faroCoreApiClient.admin.createEmptyProjectWithinCompanyGroup({
          companyId: subject.company.id,
          groupId: subject.group.id,
          project: createProjectPayload,
        });
      setCreatedProjectId(createdProject.id);
    } catch (error) {
      errorHandler({
        id: `use-create-project-${Date.now().toString()}`,
        title: "Could not create project",
        error,
      });
    }

    setIsRequestingProjectCreation(false);
  }

  return {
    isRequestingProjectCreation,
    requestCreateProject,
    createdProjectId,
  };
}
